import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, Users } from 'lucide-react';
import { SHEET_ANIMATION, BACKDROP_ANIMATION } from './sheetAnimationConfig';
/**
 * TeamSheet - Bottom sheet for selecting teams
 * Sport-agnostic component that can be used for NFL, NBA, NHL, etc.
 * Supports grouping by conference/division if provided
 */
const TeamSheet = ({ 
  isOpen, 
  onClose, 
  selectedTeam, 
  onTeamSelect,
  teams = [],
  sport = 'nfl'
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Group teams by conference if they have that property
  const groupedTeams = useMemo(() => {
    if (!teams || teams.length === 0) return {};
    
    // Check if teams have conference/group data
    const hasConferences = teams.some(team => team.conference || team.group);
    
    if (!hasConferences) {
      return { 'All Teams': teams };
    }
    
    // Group by conference
    const grouped = {};
    teams.forEach(team => {
      const conference = team.conference || team.group || 'Other';
      if (!grouped[conference]) {
        grouped[conference] = [];
      }
      grouped[conference].push(team);
    });
    
    // Sort conferences (AFC before NFC for NFL, Eastern before Western for NBA)
    const sortedGroups = {};
    Object.keys(grouped).sort().forEach(key => {
      sortedGroups[key] = grouped[key].sort((a, b) => 
        (a.name || a.displayName || '').localeCompare(b.name || b.displayName || '')
      );
    });
    
    return sortedGroups;
  }, [teams]);

  // Filter teams by search query
  const filteredGroups = useMemo(() => {
    if (!searchQuery) return groupedTeams;
    
    const query = searchQuery.toLowerCase();
    const filtered = {};
    
    Object.entries(groupedTeams).forEach(([conference, teamList]) => {
      const filteredTeams = teamList.filter(team => {
        const name = (team.name || team.displayName || '').toLowerCase();
        const city = (team.city || '').toLowerCase();
        const code = (team.code || '').toLowerCase();
        return name.includes(query) || city.includes(query) || code.includes(query);
      });
      
      if (filteredTeams.length > 0) {
        filtered[conference] = filteredTeams;
      }
    });
    
    return filtered;
  }, [groupedTeams, searchQuery]);

  const handleTeamSelect = (team) => {
    onTeamSelect(team);
    onClose();
    setSearchQuery('');
  };

  const handleClearSelection = () => {
    onTeamSelect(null);
    onClose();
    setSearchQuery('');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            {...BACKDROP_ANIMATION}
            className="fixed inset-0 bg-black/50 z-40"
            onClick={onClose}
          />
          
          {/* Sheet */}
          <motion.div
            {...SHEET_ANIMATION}
            className="fixed bottom-0 left-0 right-0 bg-gray-900 rounded-t-2xl z-50 max-h-[70vh] overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-800">
              <h3 className="text-lg font-semibold text-white">Select Team</h3>
              <button
                onClick={onClose}
                className="p-1 rounded-lg hover:bg-gray-800 transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            
            {/* Search Bar */}
            <div className="p-3 border-b border-gray-800">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search teams..."
                  className="w-full pl-10 pr-4 py-2 bg-gray-800 text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            {/* Team List */}
            <div className="overflow-y-auto max-h-[calc(70vh-140px)]">
              {/* All Teams Option */}
              <button
                onClick={handleClearSelection}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 transition-colors
                  ${!selectedTeam 
                    ? 'bg-blue-500/20 text-blue-400' 
                    : 'hover:bg-gray-800/50 text-white'
                  }
                `}
              >
                <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
                  <Users className="w-4 h-4 text-gray-400" />
                </div>
                <span className="font-medium">All Teams</span>
                {!selectedTeam && (
                  <div className="ml-auto w-2 h-2 rounded-full bg-blue-400" />
                )}
              </button>
              
              {/* Grouped Teams */}
              {Object.entries(filteredGroups).map(([conference, teamList]) => (
                <div key={conference}>
                  {Object.keys(groupedTeams).length > 1 && (
                    <div className="px-4 py-2 bg-gray-800/50">
                      <span className="text-xs font-semibold text-gray-400 uppercase">
                        {conference}
                      </span>
                    </div>
                  )}
                  
                  {teamList.map((team) => {
                    const isSelected = selectedTeam?.id === team.id;
                    const teamName = team.name || team.displayName || 'Unknown Team';
                    const teamLogo = team.logo || team.image;
                    
                    return (
                      <button
                        key={team.id}
                        onClick={() => handleTeamSelect(team)}
                        className={`
                          w-full flex items-center gap-3 px-4 py-3 transition-colors
                          ${isSelected 
                            ? 'bg-blue-500/20 text-blue-400' 
                            : 'hover:bg-gray-800/50 text-white'
                          }
                        `}
                      >
                        {teamLogo ? (
                          <img 
                            src={teamLogo} 
                            alt={teamName}
                            className="w-8 h-8 object-contain"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
                            <span className="text-xs font-bold text-gray-400">
                              {teamName.charAt(0)}
                            </span>
                          </div>
                        )}
                        
                        <div className="flex-1 text-left">
                          <div className="font-medium">{teamName}</div>
                          {team.city && (
                            <div className="text-xs text-gray-500">{team.city}</div>
                          )}
                        </div>
                        
                        {isSelected && (
                          <div className="w-2 h-2 rounded-full bg-blue-400" />
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
              
              {Object.keys(filteredGroups).length === 0 && (
                <div className="p-8 text-center">
                  <p className="text-gray-500">No teams found</p>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default TeamSheet;